


















import {
  computed, ref, onMounted, onUnmounted, watch, nextTick, defineComponent, toRefs,
} from '@nuxtjs/composition-api';
import { map } from 'lodash-es';

import getStyleProperty from '~/helper-functions/styles/getStyleProperty';

import {
  onMouseDown, startTouch, swiperMouseEvent, swiperTouchEvent,
} from './functions/eventHandlers';
import { snapSlide, prevSlide, nextSlide } from './functions/swiperTransitions';
import { ISwiperState } from './types/ISwiper';

export default defineComponent({
  props: {
    arrowsInline: {
      default: true,
      type: Boolean,
    },
    animationTime: {
      default: 300,
      type: Number,
    },
    active: {
      default: true,
      type: Boolean,
    },
    activeIndex: {
      default: 0,
      type: Number,
    },
    blockedActiveIndexWatch: {
      default: false,
      type: Boolean,
    },
    slidesClass: {
      type: String,
      default: '',
    },
    wrapClass: {
      type: String,
      default: '',
    },
    arrowsClass: {
      type: String,
      default: '',
    },
    arrowsContainerClass: {
      type: String,
      default: '',
    },
  },
  setup(props, { emit }) {
    const parent = ref<HTMLElement>();
    const wrap = ref<HTMLElement>();
    const slidesContainer = ref<HTMLElement>();
    const { activeIndex, blockedActiveIndexWatch } = toRefs(props);

    let observer: MutationObserver;
    const swiperState = ref<ISwiperState>({
      swipeStartTime: Date.now(),
      moveFinished: true,
      mousePositionStart: 0,
      parent: parent.value,
      wrap: wrap.value,
      slidesContainer: slidesContainer.value,
      slideElements: [],
      swiperPosition: 0,
      transitionDuration: 300,
      currentSlideIndex: 0,
      lastTouchPosition: 0,
      slidesPositions: [],
      parentX: 0,
      emit,
    });

    function fitLastSlide(): number {
      if (!wrap.value) {
        return 0;
      }

      let elementsLength = swiperState.value.slideElements.length;
      const swiperWidth = wrap.value.clientWidth;
      let elementsWidth = 0;

      for (let index = elementsLength - 1; index >= 0; index--) {
        const element = swiperState.value.slideElements[index];
        const marginLeft = getStyleProperty(element, 'margin-Left', 'px');
        const marginRight = getStyleProperty(element, 'margin-Right', 'px');

        const wholeElement = element.clientWidth + marginLeft + marginRight;

        if (elementsWidth + wholeElement >= swiperWidth) {
          break;
        } else {
          elementsWidth += wholeElement;
          elementsLength--;
        }
      }
      return elementsLength + 1;
    }

    function getSlidesPositions(): void {
      swiperState.value.slidesPositions = map(swiperState.value.slideElements, (slide) => slide.offsetLeft).slice(0, fitLastSlide());
    }

    function refresh(): void {
      if (!props.active || !swiperState.value.slideElements) {
        return;
      }

      swiperState.value.slideElements.length = 0;
      const arrayLen = swiperState.value.slidesContainer?.children.length ?? 0;

      for (let index = 0; index < arrayLen; index++) {
        swiperState.value.slideElements.push(swiperState.value.slidesContainer?.children[index] as HTMLElement);
      }

      swiperState.value.parentX = swiperState.value.parent?.getBoundingClientRect().left ?? 0;
      getSlidesPositions();
      swiperState.value.moveFinished = true;
      swiperState.value.mousePositionStart = 0;
      const slideIndex = swiperState.value.slideElements?.length ? swiperState.value.slideElements?.length - 1 : 0;

      if (swiperState.value.currentSlideIndex > slideIndex) {
        swiperState.value.currentSlideIndex = slideIndex;
      }

      snapSlide(swiperState.value.currentSlideIndex, swiperState.value);
    }

    async function init() {
      await nextTick();
      if (!props.active || !swiperState.value.wrap) {
        document.removeEventListener('resizeThrottled', refresh);
        swiperState.value.wrap?.removeEventListener('mousedown', swiperMouseEvent(swiperState, onMouseDown));
        swiperState.value.wrap?.removeEventListener('touchstart', swiperTouchEvent(swiperState, startTouch));
        return;
      }

      refresh();
      const isTouchDevice = computed(() => 'ontouchstart' in window || navigator.maxTouchPoints > 0);

      if (isTouchDevice.value) {
        swiperState.value.wrap?.addEventListener('touchstart', swiperTouchEvent(swiperState, startTouch));
      } else {
        swiperState.value.wrap?.addEventListener('mousedown', swiperMouseEvent(swiperState, onMouseDown));
      }

      document.addEventListener('resizeThrottled', refresh);
      snapSlide(0, swiperState.value);
      getSlidesPositions();
    }
    const swiperStyle = computed(() => {
      if (!props.active) {
        return {};
      }

      return {
        transform: `translateX(${swiperState.value.swiperPosition}px)`,
        transitionDuration: `${swiperState.value.transitionDuration}ms`,
      };
    });

    const callback = (mutations) => {
      if (mutations.length) {
        refresh();
      }
    };

    onUnmounted(() => {
      document.removeEventListener('resizeThrottled', refresh);
      observer.disconnect();
    });

    onMounted(() => {
      swiperState.value.parent = parent.value;
      swiperState.value.wrap = wrap.value;
      swiperState.value.slidesContainer = slidesContainer.value;
      if (slidesContainer.value) {
        observer = new MutationObserver(callback);
        observer.observe(slidesContainer.value, { childList: true });
      }

      init();
    });

    watch(() => props.active, init);

    watch(
      () => activeIndex.value,
      (nextActiveIndex, prevActiveIndex) => {
        if (blockedActiveIndexWatch.value) {
          if (prevActiveIndex < nextActiveIndex) {
            nextSlide(swiperState.value, nextActiveIndex - (prevActiveIndex || 0));
          } else {
            prevSlide(swiperState.value, prevActiveIndex - nextActiveIndex);
          }
        }
      },
    );

    return {
      wrap,
      parent,
      slidesContainer,
      swiperStyle,
      prevSlide: (step: number) => {
        prevSlide(swiperState.value, step);
      },
      nextSlide: (step: number) => {
        nextSlide(swiperState.value, step);
      },
      swiperState,
    };
  },
});
